import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Venue } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { TextInput } from '@/components/ui/Field';
import { looksLikePdf, prepareImage, preparePdf } from '@/lib/image';
import { ApiRequestError } from '@/lib/api';
import { useCreateVenue, useExtractVenue } from './queries';
import { nameFromUrl } from './venueLink';

/**
 * Adding a venue is one field.
 *
 * The old form asked for fourteen things before it would save anything, which is
 * the wrong shape for what actually happens: you find a place on your phone, you
 * want it on the list, and the details arrive over the next three weeks. So this
 * takes a name — or a link, which it turns into a name — and everything else is
 * filled in later on the venue's own page, where the empty slots are visible.
 */

export function AddVenue({
  wedId,
  open,
  onClose,
  onAdded,
}: {
  wedId: string;
  open: boolean;
  onClose: () => void;
  onAdded: (venue: Venue) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateVenue(wedId);
  const extract = useExtractVenue(wedId);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string>();
  const [note, setNote] = useState<string>();
  const photoInput = useRef<HTMLInputElement>(null);

  const parsed = nameFromUrl(value);

  /**
   * Reads the page and saves what it found, in that order.
   *
   * The model's answer is a proposal — it is applied through the ordinary create
   * call, and everything in it has already been through the venue schema on the
   * server. What it cannot set is a quote: a figure on a website is advertising,
   * so it comes back as words next to an empty price box.
   */
  async function addFromLink(link: string) {
    setError(undefined);
    setNote(undefined);
    try {
      const proposal = await extract.mutateAsync({ url: link });
      const fallback = nameFromUrl(link);
      const venue = await create.mutateAsync({
        ...proposal.fields,
        name: proposal.fields.name ?? fallback?.name ?? link,
        ...(fallback?.website && !proposal.fields.website ? { website: fallback.website } : {}),
        ...(proposal.inclusions.length > 0 ? { inclusions: proposal.inclusions } : {}),
        // The server composes these — the venue's own description, plus what the
        // page said about money, which belongs beside an empty price box and
        // never inside one.
        ...(proposal.notes ? { notes: proposal.notes } : {}),
      });
      setValue('');
      onClose();
      onAdded(venue);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t('venues.couldNotRead'));
    }
  }

  async function addFromPhoto(file: File) {
    setError(undefined);
    const isPdf = looksLikePdf(file);
    setNote(isPdf ? t('venues.readingPdf') : t('venues.reading'));
    try {
      // A PDF is sent whole and a photo is resized first — they are read
      // differently at the far end, so they are prepared differently here.
      const source = isPdf ? { pdf: await preparePdf(file) } : { image: await prepareImage(file) };
      const proposal = await extract.mutateAsync(source);
      const venue = await create.mutateAsync({
        ...proposal.fields,
        name: proposal.fields.name ?? t('venues.untitled'),
        ...(proposal.inclusions.length > 0 ? { inclusions: proposal.inclusions } : {}),
        ...(proposal.notes ? { notes: proposal.notes } : {}),
      });
      onClose();
      onAdded(venue);
    } catch (err) {
      // Size is the one failure worth naming: it is the person's to fix, and
      // "could not be read" would send them looking for a better scan.
      const tooLarge = err instanceof Error && err.message === 'too-large';
      setError(
        tooLarge
          ? isPdf
            ? t('venues.pdfTooBig')
            : t('venues.photoTooBig')
          : err instanceof ApiRequestError
            ? err.message
            : t('venues.couldNotRead'),
      );
    } finally {
      setNote(undefined);
      if (photoInput.current) photoInput.current.value = '';
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    const text = value.trim();
    if (!text) return;

    try {
      const venue = await create.mutateAsync(
        parsed ? { name: parsed.name, website: parsed.website } : { name: text },
      );
      setValue('');
      onClose();
      // Straight into the venue's page, which is where the rest gets filled in.
      onAdded(venue);
    } catch {
      setError(t('common.somethingWrong'));
    }
  }

  return (
    <Drawer open={open} title={t('venues.addTitle')} onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)}>
        <label className="flex flex-col gap-1">
          <span className="text-body font-medium">{t('venues.addOneField')}</span>
          <span className="text-caption text-leaf-soft">{t('venues.addOneFieldHint')}</span>
          <TextInput
            autoFocus
            value={value}
            placeholder={t('venues.addOneFieldPlaceholder')}
            onChange={(e) => setValue(e.target.value)}
          />
        </label>

        {/* Show what the link will be called, so a bad guess is caught before saving. */}
        {parsed && (
          <p className="text-caption text-leaf-soft">
            {t('venues.addFromLinkPreview', { name: parsed.name })}
          </p>
        )}

        {error && (
          <p role="alert" className="text-body text-status-declinedFg">
            {error}
          </p>
        )}

        {note && (
          <p className="text-body text-leaf-soft" role="status">
            {note}
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <Button
            type="submit"
            loading={create.isPending && !extract.isPending}
            disabled={value.trim() === ''}
          >
            {t('venues.add')}
          </Button>
          {/* Only offered for something that looks like a link, because there is
              nothing to read on a page that does not exist. */}
          {parsed && (
            <Button
              type="button"
              variant="secondary"
              loading={extract.isPending}
              onClick={() => void addFromLink(value.trim())}
            >
              {t('venues.addAndRead')}
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={() => photoInput.current?.click()}>
            {t('venues.fromFile')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>

        <input
          ref={photoInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void addFromPhoto(f);
          }}
        />
      </form>
    </Drawer>
  );
}
